import type { Column, GridApi } from 'ag-grid-community';
import { useEffect, useRef, useState, type MouseEvent } from 'react';
import styled from 'styled-components';
import { showAgGridColumnMenu } from './showAgGridColumnMenu';

const AgGridHeaderGroupMenuButtonRoot = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex: 0 0 1px;
  height: var(--ag-icon-size, 16px);
  justify-content: center;
  opacity: 0;
  overflow: hidden;
  padding: 0;
  transition:
    flex-basis 200ms ease 500ms,
    opacity 200ms ease 500ms;

  /* Keep these selectors in this component so they beat the default opacity:0 specificity. */
  .ag-header-group-cell.ag-header-active &,
  .ag-header-group-cell.ag-column-menu-visible &,
  &.ag-header-group-menu-button-visible {
    flex-basis: var(--ag-icon-size, 16px);
    opacity: 1;
    transition:
      flex-basis 200ms ease,
      opacity 200ms ease;
  }
`;

/**
 * Custom group-header menu control. Opens via {@link showAgGridColumnMenu} (private AG Grid
 * menuSvc) because public `GridApi.showColumnMenu` cannot anchor to this button — see that
 * module's docs for the internals rationale and upgrade risk.
 */
export function AgGridHeaderGroupMenuButton({
  api,
  menuColumn,
  ariaLabel = 'Open column menu',
  className,
  dataTestId,
  getHeaderGroupCell,
}: {
  api: GridApi;
  menuColumn: Column;
  ariaLabel?: string;
  className?: string;
  dataTestId?: string;
  getHeaderGroupCell?: () => Element | null;
}) {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const headerGroupCellRef = useRef<Element | null>(null);

  useEffect(() => {
    const headerGroupCell = headerGroupCellRef.current;
    if (!headerGroupCell) {
      return;
    }

    if (isMenuVisible) {
      headerGroupCell.classList.add('ag-column-menu-visible');
    } else {
      headerGroupCell.classList.remove('ag-column-menu-visible');
    }

    return () => {
      headerGroupCell.classList.remove('ag-column-menu-visible');
    };
  }, [isMenuVisible]);

  useEffect(() => {
    if (!isMenuVisible) {
      return;
    }

    const closeMenu = () => {
      setIsMenuVisible(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        api.hidePopupMenu();
        closeMenu();
      }
    };

    let hasSeenMenu = Boolean(document.querySelector('.ag-column-menu.ag-menu'));
    const observer = new MutationObserver(() => {
      const menu = document.querySelector('.ag-column-menu.ag-menu');
      if (menu) {
        hasSeenMenu = true;
        return;
      }

      if (hasSeenMenu) {
        closeMenu();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('keydown', onKeyDown);

    return () => {
      observer.disconnect();
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [api, isMenuVisible]);

  const resolveHeaderGroupCell = (event: MouseEvent<HTMLButtonElement>) => {
    const headerGroupCell = getHeaderGroupCell?.() ?? event.currentTarget.closest('.ag-header-group-cell');
    headerGroupCellRef.current = headerGroupCell;
    return headerGroupCell;
  };

  const markMenuOpened = (headerGroupCell: Element | null) => {
    headerGroupCell?.classList.add('ag-column-menu-visible');
    setIsMenuVisible(true);
  };

  const openMenuAfterButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const headerGroupCell = resolveHeaderGroupCell(event);

    const opened = showAgGridColumnMenu(
      api,
      menuColumn,
      { buttonElement: event.currentTarget, positionBy: 'button' },
      () => {
        setIsMenuVisible(false);
      }
    );
    if (opened) {
      markMenuOpened(headerGroupCell);
    }
  };

  const openMenuAfterMouseClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const headerGroupCell = resolveHeaderGroupCell(event);

    const opened = showAgGridColumnMenu(
      api,
      menuColumn,
      {
        fallbackElement: event.currentTarget,
        mouseEvent: event.nativeEvent,
        positionBy: 'mouse',
      },
      () => {
        setIsMenuVisible(false);
      }
    );
    if (opened) {
      markMenuOpened(headerGroupCell);
    }
  };

  const handleContextMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    openMenuAfterMouseClick(event);
  };

  return (
    <AgGridHeaderGroupMenuButtonRoot
      type="button"
      className={['ag-header-group-menu-button', className, isMenuVisible ? 'ag-header-group-menu-button-visible' : '']
        .filter(Boolean)
        .join(' ')}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      onClick={openMenuAfterButtonClick}
      onContextMenu={handleContextMenu}
    >
      <span className="ag-header-menu-icon" aria-hidden="true">
        <span className="ag-icon ag-icon-menu-alt" />
      </span>
    </AgGridHeaderGroupMenuButtonRoot>
  );
}
